import { useState, type KeyboardEvent, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { addDays, formatCivilDate } from '@eis/core';
import { StatusChip } from '@eis/patterns';
import {
  periodAt,
  timelineScale,
  timelineWindow,
  type TimelineEntry,
  type TimelineWindow,
} from '../model/timeline';

/** How many changed fields a period names before it says "+N". */
const NAMED_CHANGES = 2;

/**
 * The policy's periods, left to right in business time — the detail page's timeline.
 *
 * It reads as well for one period as for twenty: the strip shows the period the page reads and
 * two on either side (model/timeline.ts), says how many more there are, and offers the whole
 * timeline in an expanded view. A period says only what matters there — its dates and what
 * began it, the tarif level and the status where they begin or change, and what changed at its
 * start. Choosing a period (click, Enter, or ←/→ along the strip) reads the policy there.
 */
export function PolicyTimeline({
  entries,
  selectedValidFrom,
  today,
  locale,
  fieldLabel,
  onSelect,
}: {
  readonly entries: readonly TimelineEntry[];
  /** The first day of the period the page reads. */
  readonly selectedValidFrom: string | undefined;
  /** Today's civil date: the period that contains it is marked. */
  readonly today: string;
  readonly locale: string;
  /** The label of a product field, by its catalog key. */
  readonly fieldLabel: (key: string) => string;
  readonly onSelect: (validFrom: string) => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const [expanded, setExpanded] = useState(false);
  if (entries.length === 0) return null;

  const byStart = entries.findIndex((entry) => entry.validFrom === selectedValidFrom);
  const atToday = periodAt(entries, today);
  const selected = byStart >= 0 ? byStart : atToday >= 0 ? atToday : entries.length - 1;
  const window = timelineWindow(entries.length, selected);

  const move = (event: KeyboardEvent, index: number): void => {
    const next =
      event.key === 'ArrowLeft' ? index - 1 : event.key === 'ArrowRight' ? index + 1 : index;
    const target = entries[next];
    if (next === index || target === undefined) return;
    event.preventDefault();
    onSelect(target.validFrom);
    // The strip re-renders around the new period; keep the focus on it.
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(`[data-period="${target.validFrom}"]`)?.focus();
    });
  };

  const card = (entry: TimelineEntry, index: number, inDialog: boolean): ReactNode => (
    <PeriodCard
      key={entry.validFrom}
      entry={entry}
      selected={index === selected}
      containsToday={periodAt([entry], today) === 0}
      future={entry.validFrom > today}
      locale={locale}
      fieldLabel={fieldLabel}
      onKeyDown={(event) => {
        move(event, index);
      }}
      onClick={() => {
        if (inDialog) setExpanded(false);
        onSelect(entry.validFrom);
      }}
      dataPeriod={inDialog ? undefined : entry.validFrom}
    />
  );

  return (
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
        <Typography variant="caption" color="text.secondary" id="policy-timeline-label">
          {t('policies.cut.strip')}
        </Typography>
        {entries.length > window.end - window.start && (
          <Button
            size="small"
            onClick={() => {
              setExpanded(true);
            }}
          >
            {t('policies.cut.showAll', { count: entries.length })}
          </Button>
        )}
      </Stack>
      <CalendarScale
        entries={entries}
        today={today}
        selected={selected}
        window={window}
        locale={locale}
        onSelect={onSelect}
      />
      <Stack
        direction="row"
        role="group"
        aria-labelledby="policy-timeline-label"
        spacing={1}
        sx={{ alignItems: 'stretch', overflowX: 'auto', pb: 0.5 }}
      >
        {window.hiddenBefore > 0 && (
          <MoreStub
            label={t('policies.cut.earlier', { count: window.hiddenBefore })}
            onClick={() => {
              setExpanded(true);
            }}
          />
        )}
        {entries
          .slice(window.start, window.end)
          .map((entry, offset) => card(entry, window.start + offset, false))}
        {window.hiddenAfter > 0 && (
          <MoreStub
            label={t('policies.cut.later', { count: window.hiddenAfter })}
            onClick={() => {
              setExpanded(true);
            }}
          />
        )}
      </Stack>

      <Dialog
        open={expanded}
        onClose={() => {
          setExpanded(false);
        }}
        maxWidth="lg"
        fullWidth
        aria-labelledby="policy-timeline-all"
      >
        <DialogTitle id="policy-timeline-all">
          {t('policies.cut.allTitle', { count: entries.length })}
        </DialogTitle>
        <DialogContent>
          <Box
            role="group"
            aria-labelledby="policy-timeline-all"
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))',
              pt: 0.5,
            }}
          >
            {entries.map((entry, index) => card(entry, index, true))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() => {
              setExpanded(false);
            }}
          >
            {t('policies.cut.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/**
 * The calendar under the timeline: every period in proportion on a year/month scale, the one the
 * page reads filled, the ones on the strip tinted, today a line. A picture of the cards beside it,
 * so it is hidden from assistive technology — the cards are the control; a click on a period here
 * reads it, as the card does.
 */
function CalendarScale({
  entries,
  today,
  selected,
  window,
  locale,
  onSelect,
}: {
  readonly entries: readonly TimelineEntry[];
  readonly today: string;
  readonly selected: number;
  readonly window: TimelineWindow;
  readonly locale: string;
  readonly onSelect: (validFrom: string) => void;
}): ReactNode {
  const scale = timelineScale(entries, today);
  const track = { top: '1.25rem', height: '0.75rem' } as const;
  return (
    <Box
      aria-hidden
      data-testid="policy-timeline-scale"
      sx={{ position: 'relative', height: '3.4rem', overflow: 'hidden', mx: 0.5 }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          ...track,
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      />
      {scale.segments.map((segment) => {
        const entry = entries[segment.index];
        if (entry === undefined) return null;
        const isSelected = segment.index === selected;
        const onStrip = segment.index >= window.start && segment.index < window.end;
        const ended = entry.status === 'TERMINATED';
        return (
          <Box
            key={entry.validFrom}
            data-segment={entry.validFrom}
            onClick={() => {
              onSelect(entry.validFrom);
            }}
            sx={{
              position: 'absolute',
              left: `${String(segment.at)}%`,
              width: `${String(segment.width)}%`,
              ...track,
              cursor: 'pointer',
              boxSizing: 'border-box',
              borderRight: 2,
              borderColor: 'background.paper',
              bgcolor: isSelected
                ? 'primary.main'
                : ended
                  ? 'action.disabled'
                  : onStrip
                    ? 'primary.light'
                    : 'action.selected',
              backgroundImage: (theme) =>
                entry.validFrom > today
                  ? `repeating-linear-gradient(135deg, transparent 0 4px, ${alpha(theme.palette.background.paper, 0.45)} 4px 8px)`
                  : 'none',
              '&:hover': { opacity: 0.85 },
            }}
          />
        );
      })}
      {scale.ticks.map((tick) => (
        <Box
          key={tick.date}
          sx={{ position: 'absolute', left: `${String(tick.at)}%`, top: 0, bottom: 0 }}
        >
          {tick.yearLabelled && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                position: 'absolute',
                top: 0,
                left: 2,
                fontWeight: 600,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
              }}
            >
              {formatCivilDate(tick.date, locale, { year: 'numeric' })}
            </Typography>
          )}
          <Box
            sx={{
              position: 'absolute',
              top: '1.1rem',
              height: '1.05rem',
              borderLeft: 1,
              borderColor: 'divider',
            }}
          />
          {tick.labelled && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                position: 'absolute',
                top: '2.15rem',
                left: 2,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
              }}
            >
              {formatCivilDate(tick.date, locale, { month: 'short' })}
            </Typography>
          )}
        </Box>
      ))}
      <Box
        data-testid="policy-timeline-today"
        sx={{
          position: 'absolute',
          left: `${String(scale.today)}%`,
          top: '0.95rem',
          height: '1.35rem',
          borderLeft: 2,
          borderColor: 'text.primary',
        }}
      />
    </Box>
  );
}

function MoreStub({
  label,
  onClick,
}: {
  readonly label: string;
  readonly onClick: () => void;
}): ReactNode {
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      sx={{ flex: '0 0 auto', alignSelf: 'stretch', whiteSpace: 'nowrap', borderStyle: 'dashed' }}
    >
      {label}
    </Button>
  );
}

function PeriodCard({
  entry,
  selected,
  containsToday,
  future,
  locale,
  fieldLabel,
  onClick,
  onKeyDown,
  dataPeriod,
}: {
  readonly entry: TimelineEntry;
  readonly selected: boolean;
  readonly containsToday: boolean;
  readonly future: boolean;
  readonly locale: string;
  readonly fieldLabel: (key: string) => string;
  readonly onClick: () => void;
  readonly onKeyDown: (event: KeyboardEvent) => void;
  readonly dataPeriod: string | undefined;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const from = formatCivilDate(entry.validFrom, locale);
  const range =
    entry.validTo === ''
      ? t('policies.cut.openRange', { from })
      : `${from} – ${formatCivilDate(addDays(entry.validTo, -1), locale)}`;
  const named = entry.changedFields.slice(0, NAMED_CHANGES).map(fieldLabel);
  const more = entry.changedFields.length - named.length;
  const operation = t(`policies.cut.operation.${entry.operation}` as never, {
    defaultValue: entry.operation,
  });

  return (
    <ButtonBase
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-pressed={selected}
      // A concise name — the range and what began it; the details stay readable inside.
      aria-label={`${range}, ${operation}`}
      data-period={dataPeriod}
      focusRipple
      sx={(theme) => ({
        flex: '1 1 0',
        minWidth: '11rem',
        maxWidth: '17rem',
        // Content from the top: cards of two and three lines start their text on the same line.
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        textAlign: 'left',
        p: 1.25,
        borderRadius: 1,
        border: 1,
        borderStyle: future ? 'dashed' : 'solid',
        borderColor: selected ? 'primary.main' : 'divider',
        boxShadow: selected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      })}
    >
      <Stack spacing={0.5}>
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {range}
          </Typography>
          {containsToday && (
            <Chip size="small" variant="outlined" label={t('policies.cut.today')} />
          )}
          {future && <Chip size="small" variant="outlined" label={t('policies.cut.future')} />}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {operation}
        </Typography>
        {(entry.showStatus || entry.showTarifLevel) && (
          <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {entry.showStatus && (
              <StatusChip
                status={entry.status}
                label={t(`policies.status.${entry.status}` as never)}
              />
            )}
            {entry.showTarifLevel && (
              <Chip
                size="small"
                variant="outlined"
                label={t('policies.cut.tarifLevel', { level: entry.tarifLevel })}
              />
            )}
          </Stack>
        )}
        {named.length > 0 && (
          <Typography variant="caption">
            {t('policies.cut.changed', {
              fields:
                more > 0
                  ? `${named.join(', ')} ${t('policies.cut.more', { count: more })}`
                  : named.join(', '),
            })}
          </Typography>
        )}
        {entry.partnersChanged && (
          <Typography variant="caption">{t('policies.timeline.partnersChanged')}</Typography>
        )}
      </Stack>
    </ButtonBase>
  );
}
